import { atom } from 'jotai'

export type ApiEndpoint = {
    id: string
    urlPatterns: string[] // Support multiple patterns for flexible matching
    loaded: boolean
}

export type LoaderTrackingState = {
    apis: ApiEndpoint[]
    totalApis: number
    loadedApis: number
    progress: number
    isInitialLoadComplete: boolean
}

/**
 * GLIC2027 requires only 2 APIs to be loaded before showing the page:
 * 1. countryV2/enable/all - Country settings
 * 2. dictionary/v2/publish - Translations
 */
const REQUIRED_APIS: ApiEndpoint[] = [
    { id: 'country-settings', urlPatterns: ['countryV2/enable', 'countryV2'], loaded: false },
    { id: 'dictionary', urlPatterns: ['dictionary/v2/publish', 'dictionary'], loaded: false }
]

const getInitialApis = (): ApiEndpoint[] => {
    return [...REQUIRED_APIS]
}

const INITIAL_APIS = getInitialApis()

export const loaderTrackingState = atom<LoaderTrackingState>({
    apis: INITIAL_APIS,
    totalApis: INITIAL_APIS.length,
    loadedApis: 0,
    progress: 0,
    isInitialLoadComplete: false
})

// Mark an API as loaded
export const markApiLoaded = atom(null, (get, set, apiIdentifier: string) => {
    const currentState = get(loaderTrackingState)

    // Already complete, no need to update
    if (currentState.isInitialLoadComplete) {
        return
    }

    // Find API by URL pattern match (any pattern matches)
    const updatedApis = currentState.apis.map(api => {
        if (!api.loaded) {
            const isMatch = api.urlPatterns.some(pattern => apiIdentifier.includes(pattern))
            if (isMatch) {
                console.log(`[GLIC2027 LoaderTracking] ✅ Loaded: ${api.id}`)
                return { ...api, loaded: true }
            }
        }
        return api
    })

    const loadedCount = updatedApis.filter(api => api.loaded).length
    const progress = Math.round((loadedCount / currentState.totalApis) * 100)
    const isComplete = loadedCount === currentState.totalApis

    console.log(`[GLIC2027 LoaderTracking] Progress: ${loadedCount}/${currentState.totalApis} (${progress}%)`)

    set(loaderTrackingState, {
        apis: updatedApis,
        totalApis: currentState.totalApis,
        loadedApis: loadedCount,
        progress,
        isInitialLoadComplete: isComplete
    })
})

// Force complete the loader (fallback)
export const forceCompleteLoader = atom(null, (get, set) => {
    const currentState = get(loaderTrackingState)
    console.log('[GLIC2027 LoaderTracking] Force completing loader')
    set(loaderTrackingState, {
        ...currentState,
        progress: 100,
        isInitialLoadComplete: true
    })
})

// Reset loader state (useful for debugging or re-initialization)
export const resetLoaderTracking = atom(null, (_get, set) => {
    const apis = getInitialApis()
    set(loaderTrackingState, {
        apis: apis.map(api => ({ ...api, loaded: false })),
        totalApis: apis.length,
        loadedApis: 0,
        progress: 0,
        isInitialLoadComplete: false
    })
})
